import _ from "lodash";
import React, { useMemo, useCallback } from "react";

// Third party
import { useI18n } from "Hooks";
import { connect } from "react-redux";
import classNames from "classnames";
import { Trans } from "react-i18next";

// Utils
import { getTopPrompts, getIsAIResponseLoading } from "./modules/Utils";
import {
  customContentParser,
  safeToolArgumentsJSONParse,
} from "../../modules/Utils";
import { onEnterOrSpaceKeyDown, scrollIntoView } from "Utils";
import {
  getCanGenerateAITutorPreview,
  CODE_EDITOR_MODALITY,
  SET_TEMPLATE_FIELDS,
  TODDLE_DOC_MODALITY,
  VIDEO_EXPLORER_MODALITY,
  WORKBOOK_MODALITY,
  SEARCH,
  RUN_PYTHON_CODE,
} from "Utils/AIUtils";

// Modules
import { updateAIDataInRedux } from "Assessments/modules/AssessmentModule";
import { updateAIMessage } from "../../modules/LessonPlanningAssistantModule";

// Design system
import { Button, Avatar, Tooltip, Alert } from "@toddle-edu/ds-web";
import {
  OpenBookStarOutlined,
  StarsTwoFilled,
  TickCircleOutlined,
  WarningTriangleOutlined,
} from "@toddle-edu/ds-icons";

// Components
import AIChat from "UIComponents/AIChat";
import PromptCardsV2 from "../PromptCardsV2";
import PrerequisiteSelection from "../../components/PrerequisiteSelection";

import { AI_TUTOR_LEARNING_TOOLS } from "@toddle-edu/common-utils";

// SCSS
import classes from "./Body.scss";

const styles = {
  tooltipOverlayInnerStyle: {
    maxWidth: "200px",
  },
  bannerStyle: {
    marginTop: "4px",
    backgroundColor: "var(--surface-tertiary-enabled)",
  },
};

const Body = props => {
  const {
    messages,
    prompts,
    entityId,
    gradeId,
    setSelectedAction,
    mode,
    context,
    setContext,
    middlePaneRef,
    sourceFrom,
    portalType,
    refetchAssessments,
    initialValue,
    assessmentDetails,
    currentAIState,
    assessmentTemplate,
    courseId,
    course,
    isAITutor,
    onClickAction,
    customAIActionsList,
    updateAIDataInRedux,
    excludedFields,
    setExcludedFields,
    assessmentTypeLabel,
    onChangeSelectedPrompt,
    shouldSetInitialPrompt,
    onClickSuggestionLibrary,
    blockCustomClasses,
    blockContentCustomClasses,
    updatePromptSuggestionModalInfo,
    selectedLearningTool,
    setSelectedLearningTool,
  } = props;

  const { t } = useI18n();

  const fieldList = _.get(assessmentTemplate, "field_list", {});
  const stepList = _.get(assessmentTemplate, "step_list", {});

  const isInitialState = _.isEmpty(messages);

  const isPromptsHidden =
    isInitialState && shouldSetInitialPrompt && !_.isEmpty(initialValue);

  const emptyStateLocales = useMemo(() => {
    if (isPromptsHidden) {
      return {
        assessmentGreetMessage:
          "classRoom:toddle_ai_empty_state_greet_subtext_with_suggestion_library",
        aiTutorTitle: "ai:ai_tutor_empty_state_without_prompts_msg",
      };
    }

    return {
      assessmentGreetMessage:
        "classRoom:toddle_ai_empty_state_greet_subtext_with_label",
      aiTutorTitle: "ai:ai_tutor_empty_state_greet_msg",
    };
  }, [isPromptsHidden]);

  // ===== utils =====

  const renderEmptyState = params => {
    const { promptCountToShow } = params || {};

    const isInitialState = _.isEmpty(messages);

    // don't render initial prompts when created through AI
    const isPromptsHidden =
      isInitialState && shouldSetInitialPrompt && !_.isEmpty(initialValue);

    // hide prompt cards and button if prompts are empty
    const hasPrompts = !_.isEmpty(prompts);

    const promptsToDisplay = getTopPrompts({
      prompts,
      isAITutor,
      promptCountToShow,
      selectedLearningTool,
    });

    return (
      <div className={classes.promptsContainer}>
        <div className={classes.titleContainer}>
          <Avatar
            iconType="icon"
            icon={<StarsTwoFilled />}
            dsVersion="2.0"
            shape="circle"
            size="large"
          />
          <div className={classes.emptyStateTextContainer}>
            <span
              className={classNames("text-label-l", classes.emptyStateTitle)}
            >
              {isAITutor
                ? t(emptyStateLocales.aiTutorTitle)
                : t("classRoom:toddle_ai_empty_state_greet_text")}
            </span>

            {!isAITutor ? (
              <span
                className={classNames("text-body", classes.emptyStateSubTitle)}
              >
                {t(emptyStateLocales.assessmentGreetMessage, {
                  label: assessmentTypeLabel,
                })}
              </span>
            ) : null}
          </div>
        </div>

        {!isPromptsHidden && hasPrompts ? (
          <div className={classes.emptyState}>
            <PromptCardsV2
              prompts={promptsToDisplay}
              allPrompts={prompts}
              setSelectedAction={onChangeSelectedPrompt}
              leId={entityId}
              assessmentDetails={assessmentDetails}
              gradeId={gradeId}
              assessmentTemplate={assessmentTemplate}
              updatePromptSuggestionModalInfo={updatePromptSuggestionModalInfo}
              portalType={portalType}
              isBodyComponent={true}
              isAITutor={isAITutor}
              setSelectedLearningTool={setSelectedLearningTool}
            />
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="plain"
              isFullWidth="true"
              icon={<OpenBookStarOutlined />}
              onClick={onClickSuggestionLibrary}
            >
              {t("toddleAnalytics:browse_more_examples")}
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  const scrollToField = fieldUID => {
    const fieldElement = document.getElementById(`${fieldUID}`);
    if (fieldElement) {
      scrollIntoView({
        element: fieldElement,
        block: "center",
      });
    }
  };

  const renderUpdatedFields = ({ updatedFields }) => {
    if (_.isEmpty(updatedFields)) {
      return null;
    }

    const { updatedFieldData, erroredFieldData } = _.reduce(
      updatedFields,
      (result, field) => {
        const isErrored = _.isEmpty(field);

        const existingUpdatedFields = result.updatedFieldData || [];
        const existingErroredFields = result.erroredFieldData || [];

        const fieldLabel =
          field === "title"
            ? t("common:title")
            : _.get(fieldList, `${field}.config.label`, "");

        if (!_.isEmpty(fieldLabel)) {
          if (!isErrored && !_.find(existingUpdatedFields, { field })) {
            existingUpdatedFields.push({ field, fieldLabel });
            result.updatedFieldData = existingUpdatedFields;
          }

          if (isErrored && !_.includes(existingErroredFields, fieldLabel)) {
            // make sure it was not fixed in later tool calls
            const isFieldFixed = _.some(updatedFields, _field => {
              if (_field.uid === field) {
                return !_.isEmpty(_field.value);
              }
            });

            if (!isFieldFixed) {
              existingErroredFields.push(fieldLabel);
              result.erroredFieldData = existingErroredFields;
            }
          }
        }

        return result;
      },
      {
        updatedFieldData: [],
        erroredFieldData: [],
      }
    );

    if (_.isEmpty(updatedFieldData)) {
      return null;
    }

    const fieldNameClasses = classNames("text-body", classes.toolCallFieldName);

    return (
      <div className={classes.toolCallContainer}>
        {_.isEmpty(erroredFieldData) ? (
          <div className="text-label-l">
            {t("ai:content_added_to_your_task", {
              label: assessmentTypeLabel,
            })}
          </div>
        ) : null}

        {_.map(updatedFieldData, ({ field, fieldLabel }) => {
          return (
            <div
              key={field}
              className={classes.toolFieldContainer}
              onClick={() => scrollToField(field)}
              role="button"
              tabIndex={0}
              onKeyDown={event =>
                onEnterOrSpaceKeyDown(event, () => scrollToField(field))
              }
              aria-label={fieldLabel}
            >
              <TickCircleOutlined
                variant="success"
                size="xx-small"
                className={classes.toolCallTickIcon}
              />

              <Tooltip
                tooltip={fieldLabel}
                overlayInnerStyle={styles.tooltipOverlayInnerStyle}
              >
                <span className={fieldNameClasses}>{fieldLabel}</span>
              </Tooltip>
            </div>
          );
        })}

        {!_.isEmpty(erroredFieldData) ? (
          <div className={classes.erroredFieldsContainer}>
            <div className={classes.erroredFieldsTitle}>
              <WarningTriangleOutlined
                size="xx-small"
                variant="warning"
                className={classes.erroredFieldsIcon}
              />
              <span className="text-body">
                {t("unitPlan:fields_not_updated_subtitle", {
                  fields: _.join(erroredFieldData, ", "),
                })}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderCustomMessage = ({ message, index }) => {
    const { authorType, metadata, content, parentMessage } = message || {};

    const isLastMessage = _.size(messages) - 1 === index;
    const shouldRenderLastAction =
      isLastMessage && !getIsAIResponseLoading(currentAIState);

    switch (authorType) {
      case "prerequisite": {
        if (!shouldRenderLastAction) return null;

        return (
          <div className={classes.assistantMsg}>
            <PrerequisiteSelection
              t={t}
              prerequisiteFieldsData={metadata || {}}
              portalType={portalType}
              fieldList={fieldList}
              stepList={stepList}
              assessmentDetails={assessmentDetails}
              context={context}
              setContext={setContext}
              mode={mode}
              middlePaneRef={middlePaneRef}
              sourceFrom={sourceFrom}
              setSelectedAction={setSelectedAction}
              messages={messages}
              courseId={courseId}
              refetchAssessments={refetchAssessments}
              course={course}
              excludedFields={excludedFields}
              setExcludedFields={setExcludedFields}
              currentAIState={currentAIState}
            />
          </div>
        );
      }
      case "tool": {
        if (isAITutor) return null;

        const content = _.get(message, "content");
        const parsedContent = safeToolArgumentsJSONParse(content);
        const fieldUids = _.get(parsedContent, "response.fieldUids", []);

        return renderUpdatedFields({ updatedFields: fieldUids });
      }
      default: {
        return null;
      }
    }
  };

  const customLoadingTextRenderer = useCallback(currentAIState => {
    switch (currentAIState) {
      case SET_TEMPLATE_FIELDS: {
        return t("ai:creating_the_tutor");
      }
      case CODE_EDITOR_MODALITY:
      case TODDLE_DOC_MODALITY:
      case WORKBOOK_MODALITY:
      case VIDEO_EXPLORER_MODALITY: {
        const { label } =
          _.find(AI_TUTOR_LEARNING_TOOLS, { key: currentAIState }) || {};

        if (label) {
          return t("ai:setting_up_the_label", { label: t(label) });
        }

        return t("ai:thinking");
      }
      case SEARCH: {
        return t("ai:browsing_the_internet");
      }
      case RUN_PYTHON_CODE: {
        return t("ai:creating_an_image");
      }
    }
  }, []);

  const customAssistantTrailingMessageRenderer = message => {
    const { id, authorType, parentMessage, metadata } = message || {};
    const { isInterrupted } = metadata || {};

    const hasSetTemplateFieldsToolCall = () => {
      const parentMessageId = _.get(parentMessage, "id", null);

      const messageWithToolCalls = _.filter(messages, ({ node }) => {
        return (
          node?.parentMessage?.id === parentMessageId &&
          _.has(node?.metadata, "toolCalls")
        );
      });

      const toolCalls = _.flatMap(
        messageWithToolCalls,
        "node.metadata.toolCalls",
        []
      );

      return _.some(toolCalls, toolCall => {
        const functionName = _.get(toolCall, "function.name");
        return functionName === "set_template_fields";
      });
    };

    const handleStudentPreviewModeClick = () => {
      updateAIDataInRedux({
        showAITutorStudentPreviewModal: true,
      });
    };

    if (
      _.startsWith(id, "NEW_") ||
      authorType === "user" ||
      !getCanGenerateAITutorPreview({ assessmentDetails }) ||
      !hasSetTemplateFieldsToolCall() ||
      isInterrupted
    ) {
      return null;
    }

    return (
      <Alert
        // dsVersion="2.0"
        type="success"
        style={styles.bannerStyle}
        title={t("ai:tutor_successfully_configured")}
        message={
          <Trans i18nKey="ai:jump_into_student_preview_mode">
            <span
              role="button"
              tabIndex={0}
              className={classes.clickableText}
              onClick={handleStudentPreviewModeClick}
              onKeyDown={event =>
                onEnterOrSpaceKeyDown(event, handleStudentPreviewModeClick)
              }
              aria-label={t("ai:jump_into_student_preview_mode")}
            />
          </Trans>
        }
      />
    );
  };

  return (
    <AIChat.Body
      onClickAction={onClickAction}
      renderEmptyState={renderEmptyState}
      sourceFrom="LESSON_PLANNING_ASSISTANT"
      customAIActionsList={customAIActionsList}
      blockCustomClasses={blockCustomClasses}
      customContentParser={customContentParser}
      renderCustomMessage={renderCustomMessage}
      blockContentCustomClasses={blockContentCustomClasses}
      customLoadingTextRenderer={customLoadingTextRenderer}
      customAssistantTrailingMessageRenderer={
        customAssistantTrailingMessageRenderer
      }
    />
  );
};

const mapStateToProps = state => {
  // when coming from unit flow
  // set AI starting chat text to description of assessment
  const shouldSetInitialPrompt = _.get(
    state,
    "unitFlow.openAssessmentAIPaneOnMount",
    false
  );

  return {
    shouldSetInitialPrompt,
  };
};

const mapActionCreators = { updateAIDataInRedux, updateAIMessage };

export default connect(mapStateToProps, mapActionCreators)(Body);
